import { Menu } from "@blueprintjs/core";
import * as React from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router";
import styled from "styled-components";

import { InboxCountsCriteria, InboxRowDto, InboxStep } from "../../api";
import { useCommonHooks, useEditRouteParams } from "../../hooks";
import { ETLCodes } from "../../locales";
import InboxMenuDateCollapse from "./InboxMenuDateCollapse";

export interface IInboxMenuProps {
  onRowClick: (row: InboxRowDto) => void;
  archived?: boolean;
}

const StyledMenu = styled(Menu)`
  flex: 1;
  padding: 0 !important;
  max-width: 50%;

  /* La liste scrolle à l'intérieur de l'onglet: si c'était la page qui scrollait, le drawer
     ancré sur l'onglet défilerait avec elle. */
  height: 100%;
  min-height: 0;
  overflow: auto;

  @media (max-width: 1200px) {
    max-width: 34%;
  }
`;

// Les tranches, de la plus récente à la plus ancienne. Cet ordre et ce découpage sont ceux
// d'InboxStepRanges côté serveur: toute modification doit se faire des deux côtés.
const STEPS: { step: InboxStep; code: ETLCodes; defaultOpen?: boolean }[] = [
  { step: InboxStep.Today, code: ETLCodes.Today, defaultOpen: true },
  { step: InboxStep.Yesterday, code: ETLCodes.Yesterday, defaultOpen: true },
  { step: InboxStep.ThisWeek, code: ETLCodes.ThisWeek, defaultOpen: true },
  { step: InboxStep.LastWeek, code: ETLCodes.LastWeek },
  { step: InboxStep.TwoWeeksAgo, code: ETLCodes.TwoWeeksAgo },
  { step: InboxStep.ThreeWeeksAgo, code: ETLCodes.ThreeWeeksAgo },
  { step: InboxStep.ThisMonth, code: ETLCodes.ThisMonth },
  { step: InboxStep.LastMonth, code: ETLCodes.LastMonth },
  { step: InboxStep.ThisYear, code: ETLCodes.ThisYear },
  { step: InboxStep.LastYear, code: ETLCodes.LastYear },
  { step: InboxStep.Older, code: ETLCodes.Older },
];

export const InboxMenu: React.FunctionComponent<IInboxMenuProps> = ({
  onRowClick,
  archived = false,
}) => {
  const {
    t,
    user,
    apis: { inbox },
  } = useCommonHooks();
  const { id } = useEditRouteParams();
  const { search } = useLocation();

  const idpersonne = React.useMemo(() => {
    const value = new URLSearchParams(search).get("idpersonne");
    return !!value ? +value : undefined;
  }, [search]);

  const userId = !!id ? +id : user.userId;

  // Un seul appel pour toutes les tranches: le compteur du titre indique le total, y compris pour
  // les tranches qui ne sont pas dépliées, et permet de savoir ce qu'il reste à charger.
  const { data: counts } = useQuery(
    ["inbox-counts", userId, archived, idpersonne],
    async () =>
      await inbox.counts(
        InboxCountsCriteria.fromJS({ userId, archived, idpersonne })
      )
  );

  const countByStep = React.useMemo(
    () =>
      (counts ?? []).reduce<Partial<Record<InboxStep, number>>>(
        (acc, c) => ({ ...acc, [c.step]: c.count }),
        {}
      ),
    [counts]
  );

  return (
    <StyledMenu>
      {STEPS.map(({ step, code, defaultOpen }) => (
        <InboxMenuDateCollapse
          key={step}
          title={t(code)}
          step={step}
          count={countByStep[step]}
          onRowClick={onRowClick}
          defaultOpen={defaultOpen}
          archived={archived}
          userId={userId}
          idpersonne={idpersonne}
        />
      ))}
    </StyledMenu>
  );
};

export default InboxMenu;
